// One tooltip for the whole page, drawn in the browser's top layer so no parent's overflow, transform or stacking
// context can clip or bury it. Any element with data-tip="…" gets it on hover and keyboard focus (and on tap for
// "?" buttons); it closes on pointer leave, Escape and a press on its trigger. data-tip-side="bottom" prefers below,
// "right" beside (the collapsed sidebar). x-ui.tooltip renders data-tip. Imported for its side effect by
// every New design entry; installing twice is harmless. data-tip-href adds a "Learn more" link to the bubble
// (x-ui.term-help): the pointer can move onto the bubble to reach it, a tap opens the bubble and a tap on the link
// follows it, and clicking the "?" itself with a mouse or keyboard goes to the link.
//
// Creator cards (after Bencho's profile hover card) use the same layer: a handle with data-card='{json}'
// (App\Support\Ui\CreatorCard) grows its card out from under it on hover or focus; running down a list of handles
// slides the one card from person to person.

import { uiPlace, uiFloat, uiUnfloat } from './ui-float';

export { uiPlace, uiFloat, uiUnfloat };

function install() {
    if (window.__uiTip) return;
    window.__uiTip = true;

    const tip = document.createElement('div');
    tip.className = 'ui-tiplayer';
    tip.id = 'ui-tiplayer';
    tip.setAttribute('role', 'tooltip');
    let current = null;
    let hideT = 0;
    let lastPointer = 'mouse';

    const show = (el) => {
        const text = el.getAttribute('data-tip');
        if (!text) return;
        clearTimeout(hideT);
        current = el;
        tip.textContent = text;
        const href = el.getAttribute('data-tip-href');
        tip.classList.toggle('ui-tip-live', !!href);
        if (href) {
            const a = document.createElement('a');
            a.className = 'ui-tip-more';
            a.href = href;
            a.textContent = el.getAttribute('data-tip-more') || 'Learn more';
            tip.append(' ', a);
        }
        // On the navy shell (sidebar, top bar) the navy bubble gets a hairline ring and no arrow, so it reads as one shape
        tip.classList.toggle('ui-tip-navy', !!el.closest('.ui-sh-side, .ui-sh-bar'));
        uiFloat(tip);
        uiPlace(tip, el, { side: el.getAttribute('data-tip-side') || 'top' });
        el.setAttribute('aria-describedby', tip.id);
    };
    const hide = () => {
        clearTimeout(hideT);
        if (!current) return;
        current.removeAttribute('aria-describedby');
        current = null;
        uiUnfloat(tip);
    };
    const target = (e) => e.target instanceof Element ? e.target.closest('[data-tip]') : null;
    // a bubble with a link stays while the pointer crosses the gap onto it
    const hideSoon = () => { clearTimeout(hideT); hideT = setTimeout(hide, current?.hasAttribute('data-tip-href') ? 180 : 0); };
    tip.addEventListener('pointerenter', () => clearTimeout(hideT));
    tip.addEventListener('pointerleave', (e) => { if (!(e.relatedTarget instanceof Node && current?.contains(e.relatedTarget))) hideSoon(); });

    document.addEventListener('pointerover', (e) => {
        if (e.pointerType === 'touch') return;
        const el = target(e);
        if (el === current) clearTimeout(hideT);
        else if (el) show(el);
    });
    document.addEventListener('pointerout', (e) => {
        if (!current) return;
        if (e.relatedTarget instanceof Node && (current.contains(e.relatedTarget) || tip.contains(e.relatedTarget))) return;
        if (target(e) === current) hideSoon();
    });
    document.addEventListener('pointerdown', (e) => {
        lastPointer = e.pointerType || 'mouse';
        // pressing the trigger closes its tip (it's about to change or go somewhere); a "?" keeps its bubble
        if (current && e.target instanceof Node && current.contains(e.target) && !current.matches('.ui-q')) hide();
    }, true);
    // with a mouse or the keyboard, the "?" of a tip with a link goes to it (a tap only opens the bubble)
    document.addEventListener('click', (e) => {
        const el = e.target instanceof Element ? e.target.closest('.ui-q[data-tip-href]') : null;
        if (!el || (e.detail > 0 && lastPointer === 'touch')) return;
        window.location.href = el.getAttribute('data-tip-href');
    });
    document.addEventListener('focusin', (e) => {
        const el = target(e);
        // keyboard focus only (on the trigger or a field inside it), never the focus a mouse click leaves behind
        if (el && (el.matches(':focus-visible') || el.querySelector(':focus-visible'))) show(el);
    });
    document.addEventListener('focusout', (e) => { if (target(e) === current) hideSoon(); });
    // a tap on a "?" button shows its tip; the next tap anywhere hides it
    document.addEventListener('pointerup', (e) => {
        if (e.pointerType !== 'touch') return;
        const el = target(e);
        if (el && el.matches('.ui-q')) { el === current ? hide() : show(el); return; }
        if (e.target instanceof Node && tip.contains(e.target)) return;
        if (current) hide();
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') hide(); });
    window.addEventListener('scroll', hide, { passive: true, capture: true });
    window.addEventListener('resize', hide);
    // Livewire can swap the trigger out from under the tip
    document.addEventListener('livewire:navigated', hide);
    new MutationObserver(() => { if (current && !current.isConnected) hide(); }).observe(document.documentElement, { childList: true, subtree: true });
}

function installCards() {
    if (window.__uiCard) return;
    window.__uiCard = true;
    const card = document.createElement('div');
    card.className = 'ui-float ui-ccard';
    card.setAttribute('role', 'dialog');
    card.setAttribute('aria-live', 'polite');
    let current = null;
    let showT = 0;
    let hideT = 0;
    const el = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; if (text !== undefined) n.textContent = text; return n; };

    const fill = (d) => {
        card.replaceChildren();
        const top = el('div', 'ui-ccard-top');
        const av = el('span', 'ui-ccard-av', d.i || '');
        if (d.av) { const img = el('img'); img.src = d.av; img.alt = ''; img.loading = 'lazy'; img.onerror = () => img.remove(); av.appendChild(img); }
        const who = el('div', 'ui-ccard-who');
        who.append(el('b', '', d.n || ''));
        const sub = [d.name, d.c].filter(Boolean).join(' · ');
        if (sub) who.append(el('small', '', sub));
        top.append(av, who);
        card.append(top);
        if (d.s?.length) {
            const dl = el('dl', 'ui-ccard-st');
            d.s.forEach(([k, v]) => { const g = el('div'); g.append(el('dt', '', k), el('dd', '', v)); dl.append(g); });
            card.append(dl);
        }
    };
    const show = (trigger) => {
        let d;
        try { d = JSON.parse(trigger.getAttribute('data-card')); } catch (e) { return; }
        clearTimeout(hideT);
        const moving = current && current !== trigger && card.classList.contains('ui-float-on');
        current = trigger;
        fill(d);
        card.classList.toggle('ui-ccard-glide', !!moving);
        uiFloat(card);
        uiPlace(card, trigger, { side: 'bottom', align: 'start' });
    };
    const hide = () => { clearTimeout(showT); current = null; card.classList.remove('ui-ccard-glide'); uiUnfloat(card); };
    const later = () => { clearTimeout(hideT); hideT = setTimeout(hide, 160); };
    const target = (e) => e.target instanceof Element ? e.target.closest('[data-card]') : null;

    document.addEventListener('pointerover', (e) => {
        if (e.pointerType === 'touch') return;
        if (e.target instanceof Element && card.contains(e.target)) { clearTimeout(hideT); return; }
        const t = target(e);
        if (!t) return;
        clearTimeout(showT);
        // a moment's rest opens it; once open, the next handle swaps at once
        if (card.classList.contains('ui-float-on')) show(t);
        else showT = setTimeout(() => show(t), 220);
    });
    document.addEventListener('pointerout', (e) => {
        const t = target(e);
        const to = e.relatedTarget;
        if (to instanceof Node && (card.contains(to) || (t && t.contains(to)))) return;
        if (t || (e.target instanceof Element && card.contains(e.target))) { clearTimeout(showT); later(); }
    });
    document.addEventListener('focusin', (e) => { const t = target(e); if (t && t.matches(':focus-visible')) show(t); });
    document.addEventListener('focusout', (e) => { if (target(e) === current) later(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') hide(); });
    window.addEventListener('scroll', hide, { passive: true, capture: true });
    new MutationObserver(() => { if (current && !current.isConnected) hide(); }).observe(document.documentElement, { childList: true, subtree: true });
}

if (typeof window !== 'undefined') { install(); installCards(); }
